/**
 * Hardened replacement for Filament's `filamentOneTimeCodeInput` Alpine component.
 *
 * Filament renders the one-time-code field as N separate single-digit <input> boxes and
 * assumes the OS/browser delivers the whole code as one multi-character `input` event on the
 * first box. Native iOS/Android SMS autofill and password managers frequently don't behave
 * that way — they fire per-character events, fill only one box, emit `change` instead of
 * `input`, or scatter the digits — so codes ended up landing in the wrong boxes.
 *
 * This version keeps the vendor's manual-typing behaviour untouched, but adds paste + change
 * handling, "recompaction" of scattered digits for bulk fills, composition safety, and an
 * optional WebOTP progressive enhancement. It preserves the exact public interface the
 * (unchanged) vendor Blade relies on: `x-data="filamentOneTimeCodeInput"` / `x-modelable="state"`.
 *
 * Registered on `alpine:init` so it runs after Filament's own registration and wins. Loaded on
 * every panel page (incl. login/MFA) via resources/js/backend/app.js.
 */
const factory = () => ({
    inputs: [],

    state: null,

    otpAbortController: null,

    init() {
        this.inputs = Array.from(
            this.$root.querySelectorAll('.fi-one-time-code-input-digit'),
        )

        this.distribute(this.state)

        this.$watch('state', (state) => {
            if (this.read() === (state ?? '')) {
                return
            }

            this.distribute(state)
        })

        this.inputs.forEach((input, index) => {
            input.addEventListener('input', (event) => {
                event.stopPropagation()

                // Composition (e.g. Android GBoard) — wait for compositionend to read a
                // stable value instead of an intermediate one.
                if (event.isComposing) {
                    return
                }

                this.handleInput(event.target, index)
            })

            input.addEventListener('compositionend', (event) => {
                this.handleInput(event.target, index)
            })

            // Bulk autofill / paste (both browser SMS autofill and password managers) may
            // set .value + dispatch only a `change` event, with no usable `input`.
            input.addEventListener('change', (event) => {
                event.stopPropagation()

                if (event.target.value.length > 1) {
                    this.fill(event.target.value)

                    return
                }

                // A programmatic single-digit change still needs the state synced.
                this.commit()
            })

            input.addEventListener('paste', (event) => {
                const pasted = (
                    event.clipboardData ?? window.clipboardData
                )?.getData('text')

                if (pasted == null) {
                    return
                }

                event.preventDefault()
                this.fill(pasted)
            })

            input.addEventListener('keydown', (event) => {
                if (
                    ['Backspace', 'Delete'].includes(event.key) &&
                    event.target.value === ''
                ) {
                    this.focusInput(index - 1)

                    return
                }

                if (event.key === 'ArrowLeft') {
                    this.focusInput(index - 1)
                    event.preventDefault()
                }

                if (event.key === 'ArrowRight') {
                    this.focusInput(index + 1)
                    event.preventDefault()
                }
            })

            // Select the existing character on focus, so that typing replaces it.
            input.addEventListener('focus', (event) =>
                event.target.setSelectionRange(0, 1),
            )

            // Prevent focusing an empty input ahead of the next one to be filled.
            input.addEventListener('pointerdown', (event) => {
                let firstEmptyInputIndex = Math.min(
                    index,
                    this.getFirstEmptyInputIndex(),
                )

                if (firstEmptyInputIndex !== index) {
                    event.preventDefault()
                    this.focusInput(firstEmptyInputIndex)
                }
            })
        })

        this.listenForWebOtp()
    },

    handleInput(target, index) {
        let value = target.value

        // A value longer than one character has been autofilled or pasted, so spread it
        // across every input instead of treating it as a single digit.
        if (value.length > 1) {
            this.fill(value)

            return
        }

        if (value === '') {
            this.commit()

            return
        }

        if (/\D/.test(value)) {
            target.value = ''

            return
        }

        this.commit()
        this.focusInput(index + 1)
    },

    // Bulk fill from autofill / paste / WebOTP: strip to digits, left-pack across the boxes,
    // commit, and move focus to the first still-empty box. Recompaction here (not on manual
    // keystrokes) is what makes scattered / out-of-order autofill land correctly.
    fill(value) {
        this.distribute(value)
        this.commit()
        this.focusInput(this.getFirstEmptyInputIndex())
    },

    distribute(value) {
        let digits = (value ?? '')
            .toString()
            .replace(/\D/g, '')
            .slice(0, this.inputs.length)

        this.inputs.forEach((input, index) => {
            input.value = digits[index] ?? ''
        })
    },

    read() {
        return this.inputs.map((input) => input.value).join('')
    },

    commit() {
        let value = this.read()

        this.state = value === '' ? null : value
    },

    getFirstEmptyInputIndex() {
        let index = this.inputs.findIndex((input) => input.value === '')

        return index === -1 ? this.inputs.length - 1 : index
    },

    focusInput(index) {
        let input =
            this.inputs[Math.max(0, Math.min(index, this.inputs.length - 1))]

        if (!input) {
            return
        }

        if (document.activeElement === input) {
            input.setSelectionRange(0, 1)

            return
        }

        input.focus()
    },

    // Progressive enhancement: on supporting browsers (Chrome/Android) auto-read the incoming
    // SMS code without the user tapping anything. Never throws on unsupported browsers.
    listenForWebOtp() {
        if (!('OTPCredential' in window) || !navigator.credentials) {
            return
        }

        try {
            this.otpAbortController = new AbortController()

            navigator.credentials
                .get({
                    otp: { transport: ['sms'] },
                    signal: this.otpAbortController.signal,
                })
                .then((otp) => {
                    if (otp?.code) {
                        this.fill(otp.code)
                    }
                })
                .catch(() => {
                    // User dismissed, timed out, or unsupported — ignore silently.
                })

            // Stop listening once the form is submitted.
            this.$root
                .closest('form')
                ?.addEventListener(
                    'submit',
                    () => this.otpAbortController?.abort(),
                    { once: true },
                )
        } catch {
            // AbortController / credentials.get unavailable — ignore.
        }
    },
})

const register = () => window.Alpine.data('filamentOneTimeCodeInput', factory)

if (window.Alpine && window.Alpine.data) {
    register()
}

// Registering on alpine:init as well guarantees we run after Filament's own registration, so
// our definition is the one that wins.
document.addEventListener('alpine:init', register)
